import { Plus, Trash, PencilSimple, UploadSimple, Image, X } from "@phosphor-icons/react";
import { useRef, useState } from "react";
import API from "../../lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle
} from "../../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "../../components/ui/select";

export default function ProductsTab({ products, categories, onReload }) {
  const [productModal, setProductModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productForm, setProductForm] = useState({
    name: "", description: "", price: "", category: "",
    image_url: "", additional_images: [], stock: "100", featured: false, coming_soon: false,
    scent_notes: "", size: ""
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const multiFileInputRef = useRef(null);

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await API.post("/upload/image", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      const imageUrl = `/api/files/${data.path}`;
      setProductForm(p => ({ ...p, image_url: imageUrl }));
    } catch (err) {
      console.error("Image upload failed:", err);
      alert("Upload failed. Please try again.");
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleMultiImageUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      const formData = new FormData();
      for (let i = 0; i < files.length; i++) {
        formData.append("files", files[i]);
      }
      const { data } = await API.post("/upload/images", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      const newUrls = (data.uploaded || []).map(f => `/api/files/${f.path}`);
      setProductForm(p => ({
        ...p,
        additional_images: [...p.additional_images, ...newUrls],
        image_url: p.image_url || (newUrls.length > 0 ? newUrls[0] : p.image_url)
      }));
    } catch (err) {
      console.error("Multi-image upload failed:", err);
      alert("Upload failed. Please try again.");
    }
    setUploading(false);
    if (multiFileInputRef.current) multiFileInputRef.current.value = "";
  };

  const removeAdditionalImage = (index) => {
    setProductForm(p => ({
      ...p,
      additional_images: p.additional_images.filter((_, i) => i !== index)
    }));
  };

  const setAsMainImage = (url) => {
    setProductForm(p => ({
      ...p,
      image_url: url,
      additional_images: p.additional_images.filter(img => img !== url)
    }));
  };

  const openCreate = () => {
    setEditingProduct(null);
    const defaultCat = categories.length > 0 ? categories[0].slug : "";
    setProductForm({
      name: "", description: "", price: "", category: defaultCat,
      image_url: "", additional_images: [], stock: "100", featured: false, coming_soon: false,
      scent_notes: "", size: ""
    });
    setProductModal(true);
  };

  const openEdit = (product) => {
    setEditingProduct(product);
    const allImages = product.images || [];
    const additionalImages = allImages.filter(img => img !== product.image_url);
    setProductForm({
      name: product.name, description: product.description,
      price: String(product.price), category: product.category,
      image_url: product.image_url, additional_images: additionalImages,
      stock: String(product.stock),
      featured: product.featured, coming_soon: product.coming_soon,
      scent_notes: product.scent_notes || "", size: product.size || ""
    });
    setProductModal(true);
  };

  const handleSaveProduct = async () => {
    setSaving(true);
    try {
      const payload = {
        ...productForm,
        price: parseFloat(productForm.price) || 0,
        stock: parseInt(productForm.stock) || 0,
        images: productForm.additional_images || []
      };
      delete payload.additional_images;
      if (editingProduct) {
        await API.put(`/admin/products/${editingProduct.id}`, payload);
      } else {
        await API.post("/admin/products", payload);
      }
      setProductModal(false);
      onReload();
    } catch (err) {
      console.error("Failed to save product:", err);
    }
    setSaving(false);
  };

  const handleDelete = async (productId) => {
    if (!window.confirm("Delete this product?")) return;
    try {
      await API.delete(`/admin/products/${productId}`);
      onReload();
    } catch (err) {
      console.error("Failed to delete product:", err);
    }
  };

  return (
    <>
      <div className="flex justify-between items-center mb-8">
        <p className="text-sm text-[#A1A1AA] font-light">{products.length} products</p>
        <button onClick={openCreate} className="flex items-center gap-2 bg-[#D4AF37] text-black px-6 py-2.5 text-xs tracking-[0.15em] uppercase font-medium hover:bg-[#E5C158] transition-colors duration-300" data-testid="add-product-btn">
          <Plus size={14} /> Add Product
        </button>
      </div>
      <div className="space-y-3" data-testid="admin-products-list">
        {products.map(product => (
          <div key={product.id} className="flex items-center gap-4 bg-[#121212] border border-[rgba(212,175,55,0.1)] p-4" data-testid={`admin-product-${product.id}`}>
            <div className="w-14 h-14 flex-shrink-0 overflow-hidden bg-[#0a0a0a]">
              <img src={product.image_url} alt={product.name} className="w-full h-full object-cover" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-[#FDFBF7] font-light truncate">{product.name}</p>
              <p className="text-xs text-[#A1A1AA] font-light">{product.category} | ${product.price?.toFixed(2)} | Stock: {product.stock}</p>
            </div>
            <div className="flex items-center gap-2">
              {product.featured && <span className="text-[10px] text-[#D4AF37] border border-[#D4AF37]/30 px-2 py-0.5 uppercase tracking-wider">Featured</span>}
              <button onClick={() => openEdit(product)} className="text-[#A1A1AA] hover:text-[#D4AF37] transition-colors p-2" data-testid={`edit-product-${product.id}`}>
                <PencilSimple size={16} weight="thin" />
              </button>
              <button onClick={() => handleDelete(product.id)} className="text-[#A1A1AA] hover:text-red-400 transition-colors p-2" data-testid={`delete-product-${product.id}`}>
                <Trash size={16} weight="thin" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Product Modal */}
      <Dialog open={productModal} onOpenChange={setProductModal}>
        <DialogContent className="bg-[#121212] border-[rgba(212,175,55,0.2)] max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Cormorant_Garamond'] text-2xl font-light text-[#FDFBF7]">
              {editingProduct ? "Edit Product" : "Add Product"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4" data-testid="product-form">
            <div>
              <label className="text-xs uppercase tracking-[0.15em] text-[#A1A1AA] mb-1 block font-medium">Name</label>
              <input value={productForm.name} onChange={e => setProductForm(p => ({ ...p, name: e.target.value }))} className="w-full bg-[#0a0a0a] border-b border-white/20 px-0 py-2 text-sm text-[#FDFBF7] focus:outline-none focus:border-[#D4AF37] font-light" data-testid="product-name-input" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.15em] text-[#A1A1AA] mb-1 block font-medium">Description</label>
              <textarea value={productForm.description} onChange={e => setProductForm(p => ({ ...p, description: e.target.value }))} className="w-full bg-[#0a0a0a] border border-white/10 p-3 text-sm text-[#FDFBF7] focus:outline-none focus:border-[#D4AF37]/40 font-light min-h-[80px]" data-testid="product-description-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs uppercase tracking-[0.15em] text-[#A1A1AA] mb-1 block font-medium">Price ($)</label>
                <input type="number" step="0.01" value={productForm.price} onChange={e => setProductForm(p => ({ ...p, price: e.target.value }))} className="w-full bg-[#0a0a0a] border-b border-white/20 px-0 py-2 text-sm text-[#FDFBF7] focus:outline-none focus:border-[#D4AF37] font-light" data-testid="product-price-input" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.15em] text-[#A1A1AA] mb-1 block font-medium">Stock</label>
                <input type="number" value={productForm.stock} onChange={e => setProductForm(p => ({ ...p, stock: e.target.value }))} className="w-full bg-[#0a0a0a] border-b border-white/20 px-0 py-2 text-sm text-[#FDFBF7] focus:outline-none focus:border-[#D4AF37] font-light" data-testid="product-stock-input" />
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.15em] text-[#A1A1AA] mb-1 block font-medium">Category</label>
              <Select value={productForm.category} onValueChange={(val) => setProductForm(p => ({ ...p, category: val }))}>
                <SelectTrigger className="bg-[#0a0a0a] border-white/10 text-[#FDFBF7] text-sm" data-testid="product-category-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#121212] border-white/10">
                  {categories.map(cat => (
                    <SelectItem key={cat.slug} value={cat.slug} className="text-[#FDFBF7]">{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Main Image */}
            <div>
              <label className="text-xs uppercase tracking-[0.15em] text-[#A1A1AA] mb-1 block font-medium">Main Product Image</label>
              <div className="space-y-3">
                <div className="flex gap-3 items-center">
                  <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" onChange={handleImageUpload} className="hidden" data-testid="product-image-file-input" />
                  <button type="button" onClick={() => fileInputRef.current?.click()} disabled={uploading} className="flex items-center gap-2 border border-[#D4AF37]/30 text-[#D4AF37] px-4 py-2 text-xs tracking-[0.1em] uppercase font-medium hover:bg-[#D4AF37]/10 transition-colors duration-300 disabled:opacity-50" data-testid="upload-image-btn">
                    <UploadSimple size={14} /> {uploading ? "Uploading..." : "Upload Main"}
                  </button>
                  {productForm.image_url && (
                    <div className="w-10 h-10 border border-[#D4AF37]/30 overflow-hidden flex-shrink-0">
                      <img src={productForm.image_url} alt="Main" className="w-full h-full object-cover" />
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-[#A1A1AA] uppercase tracking-wider">Or paste URL</span>
                  <div className="flex-1 h-px bg-white/5" />
                </div>
                <input value={productForm.image_url} onChange={e => setProductForm(p => ({ ...p, image_url: e.target.value }))} className="w-full bg-[#0a0a0a] border-b border-white/20 px-0 py-2 text-sm text-[#FDFBF7] focus:outline-none focus:border-[#D4AF37] font-light" placeholder="https://..." data-testid="product-image-input" />
              </div>
            </div>

            {/* Additional Images */}
            <div>
              <label className="text-xs uppercase tracking-[0.15em] text-[#A1A1AA] mb-1 block font-medium">Additional Images</label>
              <div className="space-y-3">
                <div className="flex gap-3 items-center">
                  <input ref={multiFileInputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" multiple onChange={handleMultiImageUpload} className="hidden" data-testid="product-multi-image-input" />
                  <button type="button" onClick={() => multiFileInputRef.current?.click()} disabled={uploading} className="flex items-center gap-2 border border-white/10 text-[#A1A1AA] px-4 py-2 text-xs tracking-[0.1em] uppercase font-medium hover:border-[#D4AF37]/30 hover:text-[#D4AF37] transition-colors duration-300 disabled:opacity-50" data-testid="upload-additional-images-btn">
                    <Image size={14} /> {uploading ? "Uploading..." : "Add More Images"}
                  </button>
                  <span className="text-[10px] text-[#A1A1AA]">{productForm.additional_images.length} additional</span>
                </div>
                {productForm.additional_images.length > 0 && (
                  <div className="grid grid-cols-4 gap-2" data-testid="additional-images-grid">
                    {productForm.additional_images.map((img, i) => (
                      <div key={`addimg-${img}-${i}`} className="relative group aspect-square border border-white/10 overflow-hidden">
                        <img src={img} alt={`Additional ${i + 1}`} className="w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center gap-1">
                          <button type="button" onClick={() => setAsMainImage(img)} className="text-[#D4AF37] text-[8px] uppercase tracking-wider bg-black/60 px-1.5 py-0.5 hover:bg-[#D4AF37] hover:text-black transition-colors" data-testid={`set-main-${i}`}>Main</button>
                          <button type="button" onClick={() => removeAdditionalImage(i)} className="text-red-400 text-[8px] uppercase tracking-wider bg-black/60 px-1.5 py-0.5 hover:bg-red-500 hover:text-white transition-colors" data-testid={`remove-additional-${i}`}><X size={10} /></button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div>
              <label className="text-xs uppercase tracking-[0.15em] text-[#A1A1AA] mb-1 block font-medium">Scent Notes</label>
              <input value={productForm.scent_notes} onChange={e => setProductForm(p => ({ ...p, scent_notes: e.target.value }))} className="w-full bg-[#0a0a0a] border-b border-white/20 px-0 py-2 text-sm text-[#FDFBF7] focus:outline-none focus:border-[#D4AF37] font-light" data-testid="product-scent-input" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.15em] text-[#A1A1AA] mb-1 block font-medium">Size</label>
              <input value={productForm.size} onChange={e => setProductForm(p => ({ ...p, size: e.target.value }))} className="w-full bg-[#0a0a0a] border-b border-white/20 px-0 py-2 text-sm text-[#FDFBF7] focus:outline-none focus:border-[#D4AF37] font-light" data-testid="product-size-input" />
            </div>
            <div className="flex gap-6">
              <label className="flex items-center gap-2 text-sm text-[#A1A1AA] font-light cursor-pointer">
                <input type="checkbox" checked={productForm.featured} onChange={e => setProductForm(p => ({ ...p, featured: e.target.checked }))} className="accent-[#D4AF37]" data-testid="product-featured-checkbox" /> Featured
              </label>
              <label className="flex items-center gap-2 text-sm text-[#A1A1AA] font-light cursor-pointer">
                <input type="checkbox" checked={productForm.coming_soon} onChange={e => setProductForm(p => ({ ...p, coming_soon: e.target.checked }))} className="accent-[#D4AF37]" data-testid="product-coming-soon-checkbox" /> Coming Soon
              </label>
            </div>
            <button onClick={handleSaveProduct} disabled={saving || !productForm.name || !productForm.image_url} className="w-full bg-[#D4AF37] text-black py-3 text-xs tracking-[0.15em] uppercase font-medium hover:bg-[#E5C158] transition-colors duration-300 disabled:opacity-50 mt-4" data-testid="save-product-btn">
              {saving ? "Saving..." : editingProduct ? "Update Product" : "Create Product"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
